<template>
  <ApiLoading class="api-loading" v-if="apiStatus.refData.loading || apiStatus.refData.fail" :apiError="apiStatus.refData.fail"></ApiLoading>
  <div v-else id="main-content">
    <section v-if="submitPolicyInProcess">
      <h1>{{ submitPolicyInProcessHeader }}</h1>
      <p v-html="ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.SUBHEADER"></p>
    </section>
    <PremiumImpactInformation v-else :showPremiumInformation="false" :refData="refData">
      <template #header>
        <h1>{{ refData.confirmationSuccessHeading.informationText }}</h1>
      </template>
      <template #headerInfoText>
        <div v-html="refData.confirmationSuccessSubtitle.informationText" />
        <div>
          <p v-if="productInfo.rebateOp !== OperationType.NONE">{{ rebateText }}</p>
        </div>
      </template>
      <template #tableHeader>
        <h2>{{ refData.confirmationSuccessSubheading.informationText }}</h2>
      </template>
      <template #productPremiumContent>
        <ProductSummaryCard>
          <template #left>
            <h3>{{ productInfo.productName }}</h3>
            <p>
              <strong>{{ mapCompositionToScaleDesc(productInfo.composition) }}</strong>
            </p>

            <p v-if="productInfo.productType.toLowerCase() !== ProductType.EXTRAS_ONLY">
              <template v-if="productInfo.productExcessType === ProductExcessType.MEMBER_EXCESS">
                <strong>${{ productInfo.excess }} Hospital excess,</strong>
                is payable once per adult per financial year
              </template>
              <template v-else>
                <strong>${{ productInfo.excess }} Hospital excess,</strong>
                is payable once per policy per financial year
              </template>
            </p>

            <p>
              The next payment amount shown below does not consider any changes that may occur on the policy in the next payment period such as loading or discount removal. If any
              changes do occur between the current date and the next payment date this amount may vary.
            </p>
          </template>
          <template #right>
            <h4>Your premium</h4>
            <p class="text-green bold no-wrap">
              <span class="font-large">${{ productInfo.newPremium }}</span>
              /&nbsp;{{ productInfo.frequency }}
            </p>
            <p>
              Effective from
              <strong>{{ format(new Date(productInfo.effectiveDate), 'dd MMMM yyyy') }}</strong>
            </p>
          </template>
        </ProductSummaryCard>
      </template>
      <template #premiumAdjustmentTimeline>
        <ApiLoading
          class="api-loading"
          v-if="apiStatus.premiumAdjustmentTimeline.loading || apiStatus.premiumAdjustmentTimeline.fail"
          :apiError="apiStatus.premiumAdjustmentTimeline.fail"
        ></ApiLoading>
        <div v-else>
          <PremiumAdjustmentTimeline :refData="refData" :premiumTimelineData="premiumTimelineData" @handlePayNowBtnClick="handlePayNowBtnClick" />
        </div>
      </template>
    </PremiumImpactInformation>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { firstLetterToUpperCase } from '@general/js/utils';
import { ApiLoading, PremiumAdjustmentTimeline, PremiumImpactInformation, ProductSummaryCard } from '@/components';
import { OperationType, ProductExcessType } from '@/enums';
import { ProductType } from '@general/js/enums';
import { mapCompositionToScaleDesc } from '@/utils/CRMUtils';
import PremiumImpactServices from '@/services/PremiumImpact';
import { CHANGE_MY_COVER, MY_COVER_MAKE_A_PAYMENT } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { ErrorStrings } from '@general/js/constants';
import { generateSubmitPolicyInProcessHeader } from '@/utils/DisplayUtils';
import ChangeCoverMixins from './ChangeCoverMixins';
import CaptureInformationMixins from './CaptureInformationMixins';

export default {
  name: 'ChangeCoverConfirmation',

  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  components: {
    PremiumAdjustmentTimeline,
    ApiLoading,
    PremiumImpactInformation,
    ProductSummaryCard,
  },

  props: {
    /**
     * Flow name to be used in generating submit policy in process header.
     * This prop is only required if `submitPolicyInProcess` is true.
     */
    flowName: {
      type: String,
      required: false,
    },
    productInfo: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      apiStatus: {
        refData: {
          loading: false,
          fail: false,
        },
        premiumAdjustmentTimeline: {
          loading: false,
          fail: false,
        },
      },
      refData: {
        paymentAdjustmentInformationText: {
          informationText: '<p>The requested changes have been applied to the membership.</p><p>These will be confirmed in the certificate of cover.</p>',
        },
      },
      premiumAdjustmentPrices: null,
      premiumTimelineData: [
        {
          paymentAmount: null,
          label: 'Difference to amount paid',
        },
        {
          paymentAmount: null,
          label: 'Next payment',
          date: null,
        },
        {
          paymentAmount: null,
          label: 'Payment onwards',
          date: null,
        },
      ],
      ProductExcessType,
      ProductType,
      OperationType,
      MY_COVER_MAKE_A_PAYMENT,
      CHANGE_MY_COVER,
      ErrorStrings,
    };
  },

  beforeMount() {
    if (!this.submitPolicyInProcess) {
      this.apiStatus.refData.loading = true;
      this.getPremiumAdjustmentPrices();

      if (this.referenceData) {
        this.refData = { ...this.referenceData };
        this.refData.paymentAdjustmentInformationText = {
          title: this.referenceData.confirmationSuccessPaymentHeading.informationText,
          informationText: this.referenceData.confirmationSuccessPaymentInformationText.informationText,
        };
      }
      this.apiStatus.refData.loading = false;
      this.clearData();
    }
  },

  beforeUnmount() {
    this.clearData();
  },

  computed: {
    rebateText() {
      switch (this.productInfo?.rebateOp?.toLowerCase()) {
        case OperationType.ADD:
          return 'The rebate has been applied.';
        case OperationType.REMOVE:
          return 'The rebate has been withdrawn. Members can reapply for rebate at any time.';
        case OperationType.REPLACE:
          return 'The rebate tier has been updated.';
        case OperationType.NONE:
          return '';
        default:
          break;
      }
      return 'The rebate has been applied.';
    },

    submitPolicyInProcessHeader() {
      return generateSubmitPolicyInProcessHeader(this.flowName, this.submitPolicyInProcess);
    },
  },

  methods: {
    // Wipe off data from vuex
    clearData() {
      this.resetData();
      this.resetCAIData();
      this.resetChangeCoverData();
    },

    handlePayNowBtnClick(amt) {
      setNavigationState({ paymentAmount: amt, routeParams: { homePage: CHANGE_MY_COVER } });
      this.$router.push({ name: MY_COVER_MAKE_A_PAYMENT });
    },

    async getPremiumAdjustmentPrices() {
      this.apiStatus.premiumAdjustmentTimeline.loading = true;

      try {
        const currentMemberNumber = this.memberNumber;
        let showPremiumPaymentAmount = false;
        if (this.productInfo.oldPremium === null && this.productInfo.newPremium === null) {
          // only request premium payment amount from API if data was not available in passed props
          showPremiumPaymentAmount = true;
        }

        this.premiumAdjustmentPrices = await PremiumImpactServices.getPremiumAdjustmentPrices(currentMemberNumber, showPremiumPaymentAmount);
        this.premiumTimelineData[0].paymentAmount = this.premiumAdjustmentPrices.prorataPrice;
        if (this.productInfo.newPremium !== null) {
          this.premiumTimelineData[1].paymentAmount = parseFloat(this.productInfo.newPremium);
          this.premiumTimelineData[2].paymentAmount = parseFloat(this.productInfo.newPremium);
        } else if (this.productInfo.oldPremium !== null) {
          this.premiumTimelineData[1].paymentAmount = parseFloat(this.productInfo.oldPremium);
          this.premiumTimelineData[2].paymentAmount = parseFloat(this.productInfo.oldPremium);
        } else {
          this.premiumTimelineData[1].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;
          this.premiumTimelineData[2].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;
        }
        this.premiumTimelineData[1].date = this.premiumAdjustmentPrices.nextContributionDate;
        this.premiumTimelineData[2].date = this.premiumAdjustmentPrices.futureContributionDate;
      } catch (err) {
        logger.error(err);
        this.apiStatus.premiumAdjustmentTimeline.fail = true;
      } finally {
        this.apiStatus.premiumAdjustmentTimeline.loading = false;
      }
    },

    firstLetterToUpperCase,
    mapCompositionToScaleDesc,
    format,
  },
};
</script>

<style lang="scss" scoped></style>
